import { useLayoutEffect, useState } from 'react';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function useTooltipPosition({
  open,
  triggerRef,
  popupRef,
  placement,
  bubbleAlign,
  arrowOffsetPx,
  arrowAlign,
  arrowWidthPx,
}) {
  const arrowHalf = arrowWidthPx / 2;
  const [pos, setPos] = useState({
    left: 0,
    top: 0,
    arrowLeft: 12,
    ready: false,
  });

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (!trigger || !popup) return;

    const update = () => {
      const t = trigger.getBoundingClientRect();
      const p = popup.getBoundingClientRect();
      if (!p.width || !p.height) return;

      const viewportW = document.documentElement?.clientWidth ?? window.innerWidth;
      const gutter = 8;

      const top = placement === 'top' ? t.top - p.height + 1 : t.bottom;

      const triggerCenterX = t.left + t.width / 2;

      let left;
      if (bubbleAlign === 'arrow-offset') {
        left = triggerCenterX - (arrowOffsetPx + arrowHalf);
      } else {
        left = triggerCenterX - p.width / 2;
        if (arrowAlign === 'start') left = t.left;
        if (arrowAlign === 'end') left = t.right - p.width;
      }
      left = clamp(left, gutter, Math.max(gutter, viewportW - p.width - gutter));

      const anchorX = clamp(triggerCenterX, left + arrowHalf, left + p.width - arrowHalf);
      const arrowLeft = anchorX - left - arrowHalf;

      setPos({ left, top, arrowLeft, ready: true });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(popup);
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      resizeObserver?.disconnect();
    };
  }, [
    arrowAlign,
    arrowHalf,
    arrowOffsetPx,
    arrowWidthPx,
    bubbleAlign,
    open,
    placement,
    popupRef,
    triggerRef,
  ]);

  return pos;
}
